/**
 * CountryMultiSelect — MultiSelect preconfigured with the ISO country list and
 * flag rendering (option rows + selected tags), so every "Country" filter in
 * the app shares one look instead of re-deriving COUNTRY_OPTIONS/renderers.
 */

import MultiSelect, { type MultiSelectOption } from '@/components/MultiSelect';
import CountryFlag from '@/components/CountryFlag';
import { COUNTRY_ISO } from '@/lib/constants/countryIso';
import { fixCountryCode } from '@/lib/utils/countryCodeFix';

const COUNTRY_OPTIONS: MultiSelectOption[] = COUNTRY_ISO.map((c) => ({
  value: c.value,
  label: c.text,
}));

function renderCountryOption(opt: MultiSelectOption) {
  const cc = fixCountryCode(opt.value);
  return (
    <span className="ms-option-row">
      <CountryFlag countryCode={cc} size={16} />
      <span className="ggc-text-body">{opt.value}</span>
      <span className="ggc-text-caption-m text-ggc-text-gray">{opt.label}</span>
    </span>
  );
}

function renderCountryTag(opt: MultiSelectOption) {
  const cc = fixCountryCode(opt.value);
  return (
    <span className="ms-tag-row">
      <CountryFlag countryCode={cc} size={12} />
      <span className="ggc-text-body">{opt.value}</span>
    </span>
  );
}

export interface CountryMultiSelectProps {
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  portalled?: boolean;
}

export default function CountryMultiSelect({
  value,
  onChange,
  placeholder,
  className,
  portalled,
}: CountryMultiSelectProps) {
  return (
    <MultiSelect
      options={COUNTRY_OPTIONS}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      portalled={portalled}
      renderOption={renderCountryOption}
      renderTag={renderCountryTag}
    />
  );
}
